(() => {
    "use strict";

    // ==========================================================
    // SITE HISTORY MANAGER
    // ==========================================================

    const handlers = new Map();

    let isInitialized = false;

    const backButtons = [];
    const forwardButtons = [];

    let canGoForward =
        false;


    // ==========================================================
    // STATE HELPERS
    // ==========================================================

    function getCurrentState() {
        const state =
            window.history.state;

        if (
            state &&
            typeof state === "object"
        ) {
            return state;
        }

        return {};
    }


    function buildState(
        namespace,
        data = {}
    ) {
        return {
            ...getCurrentState(),

            frHistory: {
                namespace,
                ...data
            }
        };
    }


    // ==========================================================
    // ROUTING
    // ==========================================================

    function routeState(state) {
        const historyState =
            state &&
            typeof state === "object"
                ? state.frHistory
                : null;

        if (
            !historyState ||
            !historyState.namespace
        ) {
            handlers.forEach(
                (handler) => {
                    if (
                        handler &&
                        typeof handler.onDefault === "function"
                    ) {
                        handler.onDefault();
                    }
                }
            );

            return;
        }

        const handler =
            handlers.get(
                historyState.namespace
            );

        if (
            handler &&
            typeof handler.onState === "function"
        ) {
            handler.onState(
                historyState
            );
        }
    }


    // ==========================================================
    // MASTHEAD HISTORY BUTTONS
    // ==========================================================

    function setButtonDisabled(
        button,
        disabled
    ) {
        if (!button) {
            return;
        }

        button.disabled =
            disabled;

        button.classList.toggle(
            "is-disabled",
            disabled
        );

        button.setAttribute(
            "aria-disabled",
            disabled
                ? "true"
                : "false"
        );
    }


    function updateHistoryButtons() {
        /*
         * Browsers expose history.length, but they do not expose
         * separate backward-stack and forward-stack lengths.
         *
         * A value greater than 1 tells us that some history exists.
         * For our masthead Back control, that is sufficient.
         */
        const canGoBack =
            window.history.length >
            1;

        backButtons.forEach(
            (button) => {
                setButtonDisabled(
                    button,
                    !canGoBack
                );
            }
        );

        forwardButtons.forEach(
            (button) => {
                setButtonDisabled(
                    button,
                    !canGoForward
                );
            }
        );
    }


    function registerHistoryButtons() {
        const discoveredBackButtons =
            Array.from(
                document.querySelectorAll(
                    "[data-site-history-back]"
                )
            );

        const discoveredForwardButtons =
            Array.from(
                document.querySelectorAll(
                    "[data-site-history-forward]"
                )
            );


        discoveredBackButtons.forEach(
            (button) => {
                if (
                    backButtons.includes(
                        button
                    )
                ) {
                    return;
                }

                backButtons.push(
                    button
                );

                button.addEventListener(
                    "click",
                    () => {
                        if (
                            button.disabled
                        ) {
                            return;
                        }

                        /*
                         * Once we deliberately move backward,
                         * the browser now has a forward entry.
                         */
                        canGoForward =
                            true;

                        updateHistoryButtons();

                        window.history.back();
                    }
                );
            }
        );


        discoveredForwardButtons.forEach(
            (button) => {
                if (
                    forwardButtons.includes(
                        button
                    )
                ) {
                    return;
                }

                forwardButtons.push(
                    button
                );

                button.addEventListener(
                    "click",
                    () => {
                        if (
                            button.disabled
                        ) {
                            return;
                        }

                        window.history.forward();
                    }
                );
            }
        );


        updateHistoryButtons();
    }


    // ==========================================================
    // INITIALIZATION
    // ==========================================================

    function initialize() {
        if (isInitialized) {
            return;
        }

        isInitialized =
            true;


        window.addEventListener(
            "popstate",
            (event) => {
                routeState(
                    event.state
                );

                /*
                 * A popstate means the user has moved through
                 * browser history. Keep the controls refreshed.
                 */
                window.requestAnimationFrame(
                    updateHistoryButtons
                );
            }
        );


        if (
            document.readyState ===
            "loading"
        ) {
            document.addEventListener(
                "DOMContentLoaded",
                registerHistoryButtons,
                {
                    once: true
                }
            );

        } else {
            registerHistoryButtons();
        }


        window.addEventListener(
            "pageshow",
            () => {
                registerHistoryButtons();

                updateHistoryButtons();
            }
        );
    }


    // ==========================================================
    // FEATURE REGISTRATION
    // ==========================================================

    function register(
        namespace,
        handler = {}
    ) {
        if (!namespace) {
            throw new Error(
                "A history namespace is required."
            );
        }

        handlers.set(
            namespace,
            handler
        );

        initialize();

        return function unregister() {
            handlers.delete(
                namespace
            );
        };
    }


    // ==========================================================
    // HISTORY CHANGES
    // ==========================================================

    function push(
        namespace,
        data = {},
        url = null
    ) {
        const state =
            buildState(
                namespace,
                data
            );

        window.history.pushState(
            state,
            "",
            url ||
            window.location.href
        );

        /*
         * A new push discards any browser forward history.
         */
        canGoForward =
            false;

        updateHistoryButtons();
    }


    function replace(
        namespace,
        data = {},
        url = null
    ) {
        const state =
            buildState(
                namespace,
                data
            );

        window.history.replaceState(
            state,
            "",
            url ||
            window.location.href
        );

        updateHistoryButtons();
    }


    function back() {
        if (
            window.history.length <=
            1
        ) {
            return;
        }

        canGoForward =
            true;

        updateHistoryButtons();

        window.history.back();
    }


    function forward() {
        if (
            !canGoForward
        ) {
            return;
        }

        window.history.forward();
    }


    // ==========================================================
    // PUBLIC API
    // ==========================================================

    window.frSiteHistory = {
        register,

        push,

        replace,

        back,

        forward,

        routeCurrentState() {
            routeState(
                window.history.state
            );
        },

        getState() {
            return getCurrentState();
        },

        refreshButtons() {
            updateHistoryButtons();
        }
    };


    initialize();
})();